import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { mergeProps } from 'react-aria';

import { mergeRefs } from '../../../../lib/mergeRefs';

import { useHover } from '../../../../hooks/useHover';
import { useDraggableButton } from '../card/useDraggableButton.ts';

import { theme } from '../../../../theme/theme';
import { isDarkMode } from '../../../../theme/classes/darkMode';
import { publicUrl } from '../../../../theme/functions/publicUrl';

import { makeConnectorPath } from './makeConnectorPath';
import { connectorTypesMap, connectorTypeColorMap } from './connectorTypes';
import { executionStateType } from '../card/executionStateType';
import { useIsRTL } from '../../../../hooks/useIsRTL.ts';

const width = 22;
const maxHeight = 20;
const height = 8;

const expandedPath = makeConnectorPath({
  width: width,
  height: height,
  maxHeight: maxHeight,
  offset: 0,
});

const collapsedPath = makeConnectorPath({
  width: width,
  height: 0,
  maxHeight: maxHeight,
  offset: 0,
});

const expandedStrokePath = makeConnectorPath({
  width: width,
  height: height,
  maxHeight: maxHeight,
  offset: 1, // this is the -2px on the selected border in AdvancedFlowCard
});

const collapsedStrokePath = makeConnectorPath({
  width: width,
  height: 0,
  maxHeight: maxHeight,
  offset: 1,
});

// Types
export type ExecutionState = {
  type: string;
  // Additional fields may exist; keep open for flexibility
  [key: string]: any;
};

export interface ConnectorProps {
  dragHandleRef?: React.Ref<HTMLDivElement>;
  circleRef: React.Ref<SVGCircleElement>;
  connectorType: string;
  connectorTypeColorOverride?: string | null;
  executionState: ExecutionState;
  /**
   * Option to override the connector done state. Normally it's considered done on card start for
   * inputs and card end for outputs. Since 'all' type cards have multiple inputs which run at
   * different times they need to be able to be set as done individually. Only added support for
   * in connectors for now since we don't have multiple out connectors that run at different times.
   */

  isConnectorDone?: boolean;
  isSelected?: boolean;
  isActive?: boolean;
  isSiblingActive?: boolean;
  isDragging?: boolean;
  isDisabled?: boolean;
  isDisabledStyle?: string;
  isVisibleOverride?: boolean;
  isDraggingAndCanConnect?: boolean;
  isDragOverAndCanConnect?: boolean;
  isHovered?: boolean;
  direction?: 'start' | 'end' | 'top' | 'bottom';
  positionYOffset?: string; // e.g. '-50%'
  positionStyle?: React.CSSProperties; // manually set direction and position
  children?: React.ReactNode;
}

export const Connector = (props: ConnectorProps) => {
  const isRTL = useIsRTL();

  const isVisible =
    (props.isHovered === true && props.isDisabled !== true) ||
    props.isSelected ||
    props.isDragging ||
    props.isVisibleOverride ||
    props.isSiblingActive ||
    props.isDraggingAndCanConnect;

  const showExpandedPath = props.isActive || isVisible;
  const baseD = showExpandedPath ? expandedPath : collapsedPath;
  const strokeD = showExpandedPath ? expandedStrokePath : collapsedStrokePath;

  const pathTransition = { duration: 0.2, ease: 'easeInOut' };
  const pathAnimate = { d: baseD };
  const strokeAnimate = { d: strokeD };
  const connectorColorType = props.connectorTypeColorOverride ?? props.connectorType;

  const buttonRef = useRef<HTMLDivElement | null>(null);
  const hover = useHover({});
  const button = useDraggableButton(
    {
      elementType: 'div',
      excludeFromTabOrder: true,
    },
    buttonRef
  );

  // Allow CSS custom property in style typing
  const circleStyle = {
    '--active-circle-color': connectorTypeColorMap[connectorColorType]?.default,
  };

  let positionStyle = props.positionStyle;
  switch (props.direction) {
    case 'start':
      positionStyle = {
        position: 'absolute',
        top: '50%',
        insetInlineEnd: 'calc(100% + 1px)',
        transform: `translateY(-50%) rotate(${isRTL ? `-90deg` : `90deg`})`,
      };
      break;
    case 'end':
      const translateY = props.positionYOffset ? `calc(-50% + ${props.positionYOffset})` : '-50%';
      positionStyle = {
        position: 'absolute',
        top: '50%',
        insetInlineStart: 'calc(100% + 1px)',
        transform: `translateY(${translateY}) rotate(${isRTL ? `90deg` : `-90deg`})`,
      };
      break;
    case 'bottom':
      positionStyle = {
        position: 'absolute',
        top: 'calc(100%)',
        insetInlineEnd: '50%',
        transform: `translate(${isRTL ? '-50%' : '50%'}, 0) rotate(0deg)`,
      };
  }

  return (
    <Connector.Root style={positionStyle}>
      <Connector.ShadowCircle data-is-visible={isVisible} data-is-active={props.isActive} />
      <Connector.SVGBase
        data-is-selected={props.isSelected}
        data-is-visible={isVisible}
        data-is-active={props.isActive}
        data-is-dragover-and-can-connect={props.isDragOverAndCanConnect}
      >
        <Connector.Path transition={pathTransition} animate={pathAnimate} />
        <Connector.StrokePath transition={pathTransition} animate={strokeAnimate} />

        <Connector.Circle
          style={circleStyle}
          cx={width / 2}
          cy={2}
          r={3}
          data-is-visible={isVisible}
          data-is-active={props.isActive}
          data-is-handle-hovered={hover.isHovered}
          data-is-handle-pressed={button.isPressed}
          data-is-disabled={props.isDisabled}
          data-is-disabled-style={props.isDisabledStyle}
          data-connector-type={props.connectorType}
          data-is-connector-done={props.isConnectorDone ?? true}
          data-execution-state-type={props.executionState.type}
        />

        <circle
          ref={props.circleRef as React.Ref<SVGCircleElement>}
          cx={width / 2}
          cy={2}
          r={3}
          fill="transparent"
        />
      </Connector.SVGBase>

      {props.dragHandleRef && (
        <Connector.Handle
          {...mergeProps(button.buttonProps, hover.hoverProps)}
          ref={mergeRefs([props.dragHandleRef, buttonRef])}
          data-connector-type={props.connectorType}
          data-is-disabled={props.isDisabled}
        />
      )}

      {props.children}
    </Connector.Root>
  );
};

Connector.Root = styled.div`
  position: relative;
  // background: red;
`;

Connector.ShadowCircle = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 10px;
  height: 10px;
  transform: translateX(-50%) translateY(-75%) scale(0);
  z-index: -1;
  opacity: 0;
  transition: ${theme.duration.fast} ${theme.curve.fastIn};
  transition-property: opacity, transform;
  border-radius: 50%;
  box-shadow: ${theme.boxShadow.default};
  clip-path: inset(2px -100% -100% -100%);

  &[data-is-active='true'],
  &[data-is-visible='true'] {
    opacity: 1;
    transform: translateX(-50%) translateY(-25%) scale(1);
  }
`;

Connector.Path = styled(motion.path)`
  transition: stroke ${theme.duration.fast} ${theme.curve.fastIn};
  fill: var(--flowcard-background);
  stroke-width: 2;
`;

Connector.StrokePath = styled(motion.path)`
  fill: none;
  stroke-width: 2;
  stroke: ${theme.color.selection_border};
  stroke-opacity: 0;
  transition-property: stroke, stroke-opacity;
  // transition-duration is set lower because the border from AdvancedFlowCard is visibile under
  // this stroke. This causes two opacity colors to overlap causing a darker color. So we decrease
  // theme.duration.fast by 50ms to reduce this darker effect.
  transition-duration: calc(${theme.duration.fast} - 50ms);
  transition-timing-function: ${theme.curve.fastIn};
  transition-delay: 0ms;
`;

interface CircleProps {
  style: {
    '--active-circle-color'?: string;
  };
}

Connector.Circle = styled.circle<CircleProps>`
  fill: ${theme.color.mono_200};
  opacity: 0;

  transform: scale(0) translateY(-200%);
  transform-origin: 50% 50%;
  transform-box: fill-box;

  transition: ${theme.duration.fast} ${theme.curve.fastIn};
  transition-property: opacity, transform, fill;

  &[data-is-visible='true'] {
    opacity: 1;
    transform: scale(1);
  }

  &[data-is-active='true'] {
    opacity: 1;
    transform: scale(1);

    &[data-execution-state-type=${executionStateType.idle}] {
      fill: var(--active-circle-color);
    }
  }

  &[data-is-handle-hovered='true'] {
    fill: ${theme.color.mono_400};
  }

  &[data-is-handle-pressed='true'] {
    fill: var(--active-circle-color);
  }

  &[data-is-disabled='true'][data-is-disabled-style='unreachable'] {
    fill: ${theme.flowcard.disabled_background_color};
  }

  &:not([data-is-disabled='true'][data-is-disabled-style='unreachable']) {
    &[data-connector-type=${connectorTypesMap.In}] {
      &[data-execution-state-type=${executionStateType.prestart}] {
        fill: ${theme.flowcard.disabled_background_color};
      }

      &[data-execution-state-type=${executionStateType.start}],
      &[data-execution-state-type=${executionStateType.true}],
      &[data-execution-state-type=${executionStateType.false}],
      &[data-execution-state-type=${executionStateType.error}],
      &[data-execution-state-type=${executionStateType.end}] {
        &[data-is-active='true'][data-is-connector-done='true'] {
          fill: var(--active-circle-color);
        }
      }
    }

    &[data-connector-type=${connectorTypesMap.Out}] {
      &[data-execution-state-type=${executionStateType.start}],
      &[data-execution-state-type=${executionStateType.prestart}] {
        fill: ${theme.flowcard.disabled_background_color};
      }

      &[data-execution-state-type=${executionStateType.end}] {
        fill: var(--active-circle-color);
      }
    }

    &[data-connector-type=${connectorTypesMap.True}] {
      &[data-execution-state-type=${executionStateType.start}],
      &[data-execution-state-type=${executionStateType.prestart}] {
        fill: ${theme.flowcard.disabled_background_color};
      }

      &[data-execution-state-type=${executionStateType.true}] {
        fill: var(--active-circle-color);
      }
    }

    &[data-connector-type=${connectorTypesMap.False}] {
      &[data-execution-state-type=${executionStateType.start}],
      &[data-execution-state-type=${executionStateType.prestart}] {
        fill: ${theme.flowcard.disabled_background_color};
      }

      &[data-execution-state-type=${executionStateType.false}] {
        fill: var(--active-circle-color);
      }
    }

    &[data-connector-type=${connectorTypesMap.Error}] {
      &[data-execution-state-type=${executionStateType.start}],
      &[data-execution-state-type=${executionStateType.prestart}] {
        fill: ${theme.flowcard.disabled_background_color};
      }

      &[data-execution-state-type=${executionStateType.error}] {
        fill: var(--active-circle-color);
      }
    }
  }
`;

Connector.SVGBase = styled.svg`
  // SVG needs a width and height because else it will default to 300px, 150px. This also sizes
  // the parent.
  width: ${width}px;
  height: ${maxHeight}px;

  z-index: 1;
  overflow: visible;

  &[data-is-selected='true'] {
    ${Connector.StrokePath} {
      transition-duration: ${theme.duration.fast};
      transition-delay: 0ms;
      stroke-opacity: 1;
    }
  }

  &[data-is-dragover-and-can-connect='true'][data-is-visible='true'],
  &[data-is-dragover-and-can-connect='true'][data-is-active='true'] {
    ${Connector.StrokePath} {
      stroke: ${theme.flowcard.dragover_border};
      stroke-opacity: 1;
    }
  }
`;

Connector.Handle = styled.div`
  position: absolute;

  top: -6px;
  left: 50%;
  transform: translateX(-50%);

  width: 24px;
  height: 24px;
  border-radius: 50%;
  outline: 0;
  z-index: 10;

  // background-color: rgba(255, 128, 0, 0.5);

  &[data-connector-type=${connectorTypesMap.In}] {
    cursor: image-set(
          url(${publicUrl('img/cursor/crosshair-disabled.png')}) 1x,
          url(${publicUrl('img/cursor/crosshair-disabled@2x.png')}) 2x
        )
        8 8,
      auto;

    ${isDarkMode} {
      cursor: image-set(
            url(${publicUrl('img/cursor/darkmode/crosshair-disabled.png')}) 1x,
            url(${publicUrl('img/cursor/darkmode/crosshair-disabled@2x.png')}) 2x
          )
          8 8,
        auto;
    }
  }

  &[data-connector-type=${connectorTypesMap.Out}] {
    cursor: image-set(
          url(${publicUrl('img/cursor/crosshair-true.png')}) 1x,
          url(${publicUrl('img/cursor/crosshair-true@2x.png')}) 2x
        )
        8 8,
      auto;

    ${isDarkMode} {
      cursor: image-set(
            url(${publicUrl('img/cursor/darkmode/crosshair-true.png')}) 1x,
            url(${publicUrl('img/cursor/darkmode/crosshair-true@2x.png')}) 2x
          )
          8 8,
        auto;
    }
  }

  &[data-connector-type=${connectorTypesMap.True}] {
    cursor: image-set(
          url(${publicUrl('img/cursor/crosshair-true.png')}) 1x,
          url(${publicUrl('img/cursor/crosshair-true@2x.png')}) 2x
        )
        8 8,
      auto;

    ${isDarkMode} {
      cursor: image-set(
            url(${publicUrl('img/cursor/darkmode/crosshair-true.png')}) 1x,
            url(${publicUrl('img/cursor/darkmode/crosshair-true@2x.png')}) 2x
          )
          8 8,
        auto;
    }
  }

  &[data-connector-type=${connectorTypesMap.False}] {
    cursor: image-set(
          url(${publicUrl('img/cursor/crosshair-false.png')}) 1x,
          url(${publicUrl('img/cursor/crosshair-false@2x.png')}) 2x
        )
        8 8,
      auto;

    ${isDarkMode} {
      cursor: image-set(
            url(${publicUrl('img/cursor/darkmode/crosshair-false.png')}) 1x,
            url(${publicUrl('img/cursor/darkmode/crosshair-false@2x.png')}) 2x
          )
          8 8,
        auto;
    }
  }

  &[data-connector-type=${connectorTypesMap.Error}] {
    cursor: image-set(
          url(${publicUrl('img/cursor/crosshair-error.png')}) 1x,
          url(${publicUrl('img/cursor/crosshair-error@2x.png')}) 2x
        )
        8 8,
      auto;

    ${isDarkMode} {
      cursor: image-set(
            url(${publicUrl('img/cursor/darkmode/crosshair-error.png')}) 1x,
            url(${publicUrl('img/cursor/darkmode/crosshair-error@2x.png')}) 2x
          )
          8 8,
        auto;
    }
  }

  &[data-is-disabled='true'] {
    cursor: unset;
  }
`;
